/**
 * SuggestedDealsReviewSheet — after a planning run, review AI-suggested deals.
 * Actions per deal: Approve · Edit · Try again (with feedback) · Reject.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, RefreshCw, Loader2, Sparkles, X } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import DealCard from './DealCard';
import { useAuth } from '../../context/AuthContext';

const FEEDBACK_PRESETS = [
  'Simpler',
  'Avoid discounting',
  'Focus on offices',
  'More local flavour',
  'Higher margin',
  'Less operational effort',
];

export default function SuggestedDealsReviewSheet({ runId, open, onOpenChange }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [tryAgainDealId, setTryAgainDealId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [notes, setNotes] = useState([]);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/deals/planning-runs/${runId}`);
      setSuggestions(res.data.suggestions || []);
      setNotes(res.data.run?.notes || []);
    } catch (e) {
      toast.error('Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open && runId) reload(); /* eslint-disable-next-line */ }, [open, runId]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      const res = await api.post(`/deals/${id}/approve`);
      if (res.data.warnings?.length) {
        res.data.warnings.forEach((w) => toast.warning(w));
      }
      toast.success('Approved');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    } finally { setBusyId(null); }
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/deals/${id}/reject`);
      toast.success('Rejected');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reject');
    } finally { setBusyId(null); }
  };

  const runTryAgain = async () => {
    if (!tryAgainDealId) return;
    setBusyId(tryAgainDealId);
    try {
      await api.post(`/deals/${tryAgainDealId}/try-again`, { feedback });
      toast.success('New suggestion generated');
      setTryAgainDealId(null);
      setFeedback('');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI could not regenerate. Try again.');
    } finally { setBusyId(null); }
  };

  const pending = suggestions.filter((s) => s.status === 'ai_suggested');
  const reviewed = suggestions.filter((s) => s.status !== 'ai_suggested');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="review-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> AI Suggestions
          </SheetTitle>
          <SheetDescription>
            Approve the ones you like, reject the rest, or ask the AI to try again with feedback.
          </SheetDescription>
        </SheetHeader>

        {notes && notes.length > 0 && (
          <div className="my-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
            {notes.join(' · ')}
          </div>
        )}

        {loading ? (
          <div className="p-10 text-center text-muted-foreground">
            <Loader2 className="w-8 h-8 mx-auto animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            <div>
              <div className="text-sm font-medium mb-2">
                Pending review ({pending.length})
              </div>
              {pending.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing left to review.</p>
              ) : (
                <div className="space-y-3">
                  {pending.map((d) => (
                    <div key={d.id} data-testid={`suggestion-${d.id}`}>
                      <DealCard
                        deal={d}
                        actions={
                          <>
                            <Button
                              size="sm"
                              onClick={() => approve(d.id)}
                              disabled={busyId === d.id}
                              data-testid={`approve-${d.id}`}
                            >
                              {busyId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setTryAgainDealId(d.id); setFeedback(''); }}
                              disabled={busyId === d.id}
                              data-testid={`tryagain-${d.id}`}
                            >
                              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Try again
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => reject(d.id)}
                              disabled={busyId === d.id}
                              data-testid={`reject-${d.id}`}
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </>
                        }
                      />
                      {tryAgainDealId === d.id && (
                        <div className="mt-2 p-3 border rounded bg-accent/30 space-y-2">
                          <div className="text-xs font-medium">What would you change?</div>
                          <div className="flex flex-wrap gap-1">
                            {FEEDBACK_PRESETS.map((p) => (
                              <button
                                key={p}
                                onClick={() => setFeedback((s) => (s ? `${s} · ${p}` : p))}
                                className="text-[11px] px-2 py-0.5 rounded-full border hover:bg-background"
                              >{p}</button>
                            ))}
                          </div>
                          <Textarea
                            rows={2}
                            placeholder="Optional custom feedback…"
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            data-testid={`feedback-input-${d.id}`}
                          />
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => setTryAgainDealId(null)}>
                              <X className="w-3.5 h-3.5 mr-1" /> Cancel
                            </Button>
                            <Button size="sm" onClick={runTryAgain} disabled={busyId === d.id} data-testid={`send-tryagain-${d.id}`}>
                              {busyId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                              Regenerate
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {reviewed.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2 mt-6">
                  Already reviewed ({reviewed.length})
                </div>
                <div className="space-y-3">
                  {reviewed.map((d) => (
                    <DealCard key={d.id} deal={d} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
